// The watch layout shared by /g/:id and /s/:token (DS PlayerScreen / WatchPage, SD35): light,
// the video column on the left and the Transcript / Chapters card on the right. Two variants:
// `app` is the signed-in player inside the AppShell (its header carries the title, the ghost
// action row sits under the video); `public` is the recipient's page (brand header, the title
// and meta under the video, pill tabs). The kit's Comments tab, reactions and "views" meta are
// deliberately absent (FR-EN7).
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Icon, Logo, VideoFrame } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { GrackleVisibility, WatchOut } from "../api/types";
import type { GrackleTranscript } from "../live/types";
import { formatDuration, formatRelativeTime, visibilityIcon, visibilityLabel } from "../library/format";
import { HoldBadge } from "../components/ui";
import type { PlayerHandle, ResumeOffer } from "./Player";
import CappedState from "./CappedState";
import ResumeChip from "./ResumeChip";
import TranscriptPanel from "./TranscriptPanel";
import type { ChaptersResponse } from "./chapters";
import { usePlayableSource } from "./playableSource";
import { useSnapshotFrame } from "./snapshots";
import { cuesFromTranscript, toVtt } from "./transcript";
import "./watch.css";

// hls.js is the single largest dependency in this app (~250 kB gzipped) and only the
// watch/share screens need it — code-split it out of the main bundle rather than paying for
// it on every auth/library page load.
const Player = lazy(() => import("./Player"));

export type WatchVariant = "app" | "public";

/** How long "Copied" stays on the Copy link button. */
const COPIED_MS = 2000;

/** "Copy link" (DS PlayerScreen / WatchPage header): copies the current page URL and confirms
 * with "Copied" for a moment. Secondary, so the one purple action stays Share. */
export function CopyLinkButton({ size = "md", url }: { size?: "sm" | "md"; url?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  function copy() {
    const href = url ?? window.location.href;
    try {
      void navigator.clipboard?.writeText(href).catch(() => {});
    } catch {
      // Clipboard API unavailable (insecure context); the address bar still has the link.
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  }
  return (
    <Button variant="secondary" size={size} icon={copied ? "check" : "link-simple"} onClick={copy} aria-live="polite">
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

/** Visibility as the DS badge, as the library cards draw it: the level's word and icon, never
 * colour alone. Changed from Share, not here. */
export function VisibilityBadge({ visibility }: { visibility: GrackleVisibility }) {
  return (
    <Badge
      size="sm"
      tone={visibility === "private" ? "private" : "shared"}
      icon={visibilityIcon(visibility)}
      aria-label={`Who can watch: ${visibilityLabel(visibility)}`}
    >
      {visibilityLabel(visibility)}
    </Badge>
  );
}

/** The public watch page's header (DS WatchPage): the lockup, "Shared by …", and either My
 * library (signed in) or the one purple Sign up free. No "Reply with a video" (FR-EN7). */
export function WatchPublicHeader({ owner, signedIn }: { owner?: string | null; signedIn: boolean }) {
  return (
    <header className="watch-public__top">
      <Link to={signedIn ? "/orgs" : "/signin"} className="watch__brand" aria-label="Grackle">
        <Logo height={24} />
      </Link>
      <div className="watch-public__spacer" />
      {owner ? <span className="watch-public__by">Shared by {owner}</span> : null}
      {signedIn ? (
        <Link to="/orgs" className="gk-btn gk-btn--secondary gk-btn--sm">
          My library
        </Link>
      ) : (
        <Link to="/signup" className="gk-btn gk-btn--primary gk-btn--sm">
          Sign up free
        </Link>
      )}
    </header>
  );
}

/** "Owner · 4:12 · 2 hours ago", who can watch (when known — editors only), and the legal-hold
 * chip. */
export function WatchMeta({
  grackle,
  visibility,
}: {
  grackle: WatchOut["grackle"];
  visibility?: GrackleVisibility | null;
}) {
  return (
    <div className="watch__meta">
      <span>{grackle.owner_name}</span>
      {grackle.duration_ms != null ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="gk-num">{formatDuration(grackle.duration_ms)}</span>
        </>
      ) : null}
      <span aria-hidden="true">·</span>
      <span>{formatRelativeTime(grackle.created_at)}</span>
      {visibility ? <VisibilityBadge visibility={visibility} /> : null}
      {grackle.on_legal_hold ? <HoldBadge /> : null}
    </div>
  );
}

/** FR-P9's re-run action for an editor: re-queues the publish job (POST /grackles/{id}/republish). */
function RetryPublish({ grackleId, onQueued }: { grackleId: string; onQueued?: () => unknown }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function retry() {
    setBusy(true);
    setError(null);
    try {
      await api("POST", `/grackles/${grackleId}/republish`);
      await onQueued?.();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="watch-status__body">
        Something went wrong while publishing this recording. The original upload is safe, so you can try again.
      </div>
      <Button variant="inverse" size="sm" icon="arrow-clockwise" disabled={busy} onClick={() => void retry()}>
        {busy ? "Retrying…" : "Try again"}
      </Button>
      {error ? <div className="watch-status__body">{error}</div> : null}
    </>
  );
}

export default function WatchView({
  watch,
  transcript,
  userId,
  variant = "public",
  topbar,
  actions,
  visibility,
  notices,
  transcriptHint,
  chapters,
  onChaptersChange,
  captionsFor,
  initialSeekSeconds,
  onSeeked,
  notes,
  onTranscriptChange,
  onReloadWatch,
  onNotesRegenerate,
}: {
  watch: WatchOut;
  transcript: GrackleTranscript | null;
  /** Signed-in viewer, for the per-account resume point */
  userId?: string | null;
  /** `app`: inside the AppShell, the header owns the title. `public`: brand header, title under
   * the video, pill tabs. */
  variant?: WatchVariant;
  /** The page header: the player header (app) or the brand bar (public). */
  topbar: ReactNode;
  /** The ghost-button row under the video (editor actions on the app variant). */
  actions?: ReactNode;
  /** Known visibility (editors only — WatchOut doesn't carry it); shows the badge. */
  visibility?: GrackleVisibility | null;
  /** Errors from top-bar actions, shown above the video */
  notices?: ReactNode;
  transcriptHint?: string;
  /** F12: `GET /grackles/{id}/chapters`; null until loaded (or when it failed). */
  chapters?: ChaptersResponse | null;
  /** F12 scope 2: told about every chapters mutation (add/rename/nudge/delete/clear), so the
   * parent's `chapters` state — shared by this Player and the Chapters tab — stays in sync. */
  onChaptersChange?: (chapters: ChaptersResponse) => void;
  /** F12: show caption download links (the share token keeps link viewers authorized). */
  captionsFor?: { grackleId: string; share?: string | null };
  /** `?t=<seconds>` deep link. F2: a transcript search hit links to /g/:id?t=; seeks once the
   * player is mounted, and again whenever the value changes (another hit for the same
   * recording). F1: the page skips its own `?t=` rewrites (see `onSeeked`), so those don't
   * re-seek. */
  initialSeekSeconds?: number | null;
  /** F1: told about every transcript/notes seek, so the page can keep `?t=` current. */
  onSeeked?: (seconds: number) => void;
  /** F1: meeting notes, rendered under the title; given the same seek the transcript uses. */
  notes?: (onSeek: (seconds: number) => void) => ReactNode;
  /** Re-fetches `watch`; lets the player re-sign an expired progressive URL (FR-ST18). */
  onReloadWatch?: () => unknown;
  /** FR-M5a: told the transcript the server returned after a speaker rename, so the parent's
   * `transcript` state (cues, captions, this panel) follows without a refetch. */
  onTranscriptChange?: (transcript: GrackleTranscript) => void;
  /** FR-M5b: the transcript panel started regenerating notes after a speaker merge. */
  onNotesRegenerate?: () => void;
}) {
  const { grackle } = watch;
  const playerRef = useRef<PlayerHandle>(null);
  const [resume, setResume] = useState<ResumeOffer | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const cues = useMemo(() => cuesFromTranscript(transcript), [transcript]);
  // The captions track is the stored VTT itself when there is one; a live meeting's timed lines
  // are turned into VTT here. Either way it's an inline blob, never a second signed URL.
  const captionsVtt = useMemo(() => {
    if (cues.length === 0) return null;
    return transcript?.vtt && transcript.vtt.trim() ? transcript.vtt : toVtt(cues);
  }, [cues, transcript]);
  const onSeek = useCallback(
    (t: number) => {
      playerRef.current?.seek(t);
      onSeeked?.(t);
    },
    [onSeeked],
  );

  // `?t=` deep link (F1 + F2): the player is lazy-loaded, so wait for its handle, then seek
  // once per value.
  useEffect(() => {
    if (initialSeekSeconds == null || !Number.isFinite(initialSeekSeconds)) return;
    const id = setInterval(() => {
      if (!playerRef.current) return;
      playerRef.current.seek(initialSeekSeconds);
      clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [initialSeekSeconds]);

  const notesNode = notes ? notes(onSeek) : null;
  const { source, refresh } = usePlayableSource(watch, onReloadWatch);
  // SD65: only an audio-only recording (FR-P14) has snapshots to stand in for the picture.
  const snapshot = useSnapshotFrame(
    grackle.remux_state === "not_applicable" ? watch.snapshots : null,
    currentTime,
  );

  return (
    <div className={`watch watch--${variant}`}>
      {topbar}
      <div className="watch__body">
        <main className="watch__main">
          {notices}
          {/* FR-PL1b (SD25): exactly one of these two URLs is present on a ready grackle —
              HLS once the remux has produced segments, a signed URL to the master itself
              while FR-P12's remux is still only an option someone has to ask for. SD60: a
              processing grackle already plays from its master (playableSource.ts). */}
          {watch.capped ? (
            <CappedState title={grackle.title} posterUrl={watch.poster_url} capped={watch.capped} />
          ) : source ? (
            <Suspense fallback={<VideoFrame tone="dark" />}>
              <Player
                ref={playerRef}
                src={source.src}
                kind={source.kind}
                onSourceExpired={refresh}
                poster={watch.poster_url}
                title={grackle.title}
                recordingId={grackle.id}
                userId={userId}
                durationMs={grackle.duration_ms}
                captionsVtt={captionsVtt}
                captionsLang={transcript?.language}
                onTime={setCurrentTime}
                sourceWidth={grackle.source_width}
                sourceHeight={grackle.source_height}
                renditions={watch.renditions}
                chapters={chapters?.chapters}
                snapshot={snapshot}
                onResumeOffer={setResume}
              />
            </Suspense>
          ) : grackle.status === "failed" ? (
            <VideoFrame tone="dark">
              <div className="watch-status" role="status">
                <Icon name="warning-circle" size={26} />
                <div className="watch-status__title">Processing failed</div>
                {watch.can_edit ? (
                  <RetryPublish grackleId={grackle.id} onQueued={onReloadWatch} />
                ) : (
                  <div className="watch-status__body">
                    Something went wrong while publishing this recording. The original upload is safe — ask
                    the owner to try again.
                  </div>
                )}
              </div>
            </VideoFrame>
          ) : (
            <VideoFrame tone="dark">
              <div className="watch-status" role="status">
                <Icon name="circle-notch" size={26} className="spin" />
                <div className="watch-status__title">
                  {grackle.status === "uploading" ? "Still uploading" : "Still processing"}
                </div>
                <div className="watch-status__body">This page updates on its own the moment it's ready to watch.</div>
              </div>
            </VideoFrame>
          )}

          {variant === "public" ? (
            <div className="watch__head">
              <div className="watch__head-titles">
                <h1 className="watch__title">{grackle.title}</h1>
                <WatchMeta grackle={grackle} visibility={visibility} />
              </div>
              <CopyLinkButton size="sm" />
            </div>
          ) : null}

          {actions || resume ? (
            <div className="watch__actions-row">
              {/* The resume offer sits in the row under the video rather than over the picture;
                  the embed and mobile players, which have no row, keep the overlay. */}
              {resume ? <ResumeChip at={resume.at} onResume={resume.accept} onDismiss={resume.decline} /> : null}
              {actions}
            </div>
          ) : null}

          {grackle.description ? <p className="watch__description">{grackle.description}</p> : null}

          {/* F1: notes under the title (DS WatchPage draws the summary card here). A summary
              line always carries its source timestamp (FR-M7). */}
          {notesNode ? <div className="watch__notes">{notesNode}</div> : null}
          {/* F13a: no "Public viewing" usage card here (FR-EN7 — no viewer analytics on the
              recording view); the per-recording cap lives in the share dialog's link protection. */}
        </main>

        <TranscriptPanel
          tabsVariant={variant === "public" ? "pill" : "underline"}
          status={grackle.transcript_status}
          transcript={transcript}
          cues={cues}
          currentTime={currentTime}
          onSeek={onSeek}
          emptyHint={transcriptHint}
          chapters={chapters?.chapters ?? []}
          grackleId={grackle.id}
          canEdit={watch.can_edit}
          onChaptersChange={onChaptersChange}
          onTranscriptChange={onTranscriptChange}
          onNotesRegenerate={onNotesRegenerate}
          captionsFor={captionsFor}
        />
      </div>
    </div>
  );
}
